import React, { useCallback, useMemo, useState } from "react";
import { DocumentCategory } from "@common/types/documentCategory";
import { WorkspaceAction } from "@common/userWorkspaceRoles";
import { Side, Align } from "@design-system/overlay/overlayPositioning";
import type { TooltipTriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { PlainModal } from "@design-system/Modal/Modal";
import {
    Picker,
    pickerTreeRowPlacements,
} from "@design-system/Picker/Picker.lib";
import { CreateDocumentCategoryModal } from "@components/settings/DocumentCategories/CreateDocumentCategoryModal";
import { useCanPerformWorkspaceAction } from "@hooks/permissions/useCanPerformWorkspaceAction";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { PlusIcon } from "@icons";
import { useAssignableDocumentCategories } from "@api/documentCategory.api";
import { buildCategorySearchLabels } from "@components/transactions/TransactionSplitV2/buildCategorySearchLabels";
import { ComboboxTriggerButton } from "@components/transactions/TransactionSplitV2/ComboboxTriggerButton";
import { HStack } from "@design-system/HStack/HStack";
import { DocumentCategoryIcon } from "../DocumentCategoryIcon";

interface Props {
    selectedCategory?: Pick<
        DocumentCategory,
        "id" | "label" | "documentTaxonomy" | "parentCategoryId" | "workspaceId"
    > | null;
    onChange: (category: DocumentCategory | null) => void;
    disabled?: boolean;
    placeholder?: string;
    readOnly?: boolean;
    hideArrow?: boolean;
    className?: string;
    triggerButtonProps?: TooltipTriggerButtonProps;
    onOpenChange?: (open: boolean) => void;
    onCreationModalOpenChange?: (open: boolean) => void;
    hideFooter?: boolean;
    open?: boolean;
    /** A Picker trigger part (e.g. `Picker.CellContent`) replacing the default button. */
    trigger?: React.ReactNode;
    /** The popup's accessible name (a grid column's name). */
    popupLabel?: string;
    side?: Side;
    align?: Align;
}

export const DocumentCategoryCombobox: React.FC<Props> = ({
    selectedCategory,
    onChange,
    disabled,
    placeholder = "Select category",
    readOnly,
    hideArrow,
    className,
    triggerButtonProps,
    onOpenChange,
    onCreationModalOpenChange,
    hideFooter,
    open,
    trigger,
    popupLabel,
    side,
    align,
}) => {
    const categories: DocumentCategory[] = useAssignableDocumentCategories();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [showCreationModal, setShowCreationModal] = useState(false);
    const [typedSearch, setTypedSearch] = useState<string>();
    const [creationName, setCreationName] = useState<string>();

    const canManageCustomCategories = useCanPerformWorkspaceAction(
        WorkspaceAction.CUSTOM_CATEGORIES_EDIT,
    );

    const categoriesById = useMemo(
        () => new Map(categories.map((category) => [category.id, category])),
        [categories],
    );
    const selectedCategoryId =
        selectedCategory && categoriesById.has(selectedCategory.id)
            ? selectedCategory.id
            : null;

    const categorySearchLabels = useMemo(
        () =>
            buildCategorySearchLabels(categories, (category) =>
                category.label.toLowerCase(),
            ),
        [categories],
    );

    const filteredCategories = useMemo(() => {
        const normalizedSearch = typedSearch?.trim().toLowerCase();
        if (!normalizedSearch) {
            return categories;
        }

        return categories.filter((category) => {
            const searchLabel = categorySearchLabels.get(category.id) ?? "";
            return searchLabel.includes(normalizedSearch);
        });
    }, [categories, typedSearch, categorySearchLabels]);

    const rowPlacements = useMemo(
        () =>
            pickerTreeRowPlacements(
                filteredCategories.map((category) =>
                    category.parentCategoryId == null ? 0 : 1,
                ),
            ),
        [filteredCategories],
    );

    const onCreateNew = useCallback(() => {
        setCreationName(typedSearch);
        setIsDropdownOpen(false);
        setShowCreationModal(true);
        onCreationModalOpenChange?.(true);
    }, [onCreationModalOpenChange, typedSearch]);

    const onCreationModalClosed = useCallback(
        (createdCategory?: DocumentCategory) => {
            setShowCreationModal(false);
            if (createdCategory) {
                onChange(createdCategory);
            }
            onCreationModalOpenChange?.(false);
        },
        [onChange, onCreationModalOpenChange],
    );

    const canShowCta = canManageCustomCategories;

    return (
        <>
            <Picker.Select
                searchable
                value={selectedCategoryId}
                onValueChange={(categoryId) =>
                    onChange(
                        categoryId === null
                            ? null
                            : (categoriesById.get(categoryId) ?? null),
                    )
                }
                onInputValueChange={(inputValue) => {
                    setTypedSearch(inputValue || undefined);
                }}
                onOpenChange={(isOpen) => {
                    setIsDropdownOpen(isOpen);
                    onOpenChange?.(isOpen);
                }}
                disabled={disabled || readOnly}
                open={open ?? isDropdownOpen}
            >
                {trigger ?? (
                    <ComboboxTriggerButton
                        buttonProps={triggerButtonProps}
                        className={className}
                        ariaLabel={selectedCategory?.label}
                        value={
                            selectedCategory ? (
                                <HStack gap={4} align="center">
                                    <DocumentCategoryIcon
                                        documentTaxonomy={
                                            selectedCategory.documentTaxonomy
                                        }
                                    />
                                    <BreakableText
                                        text={selectedCategory.label}
                                        showTooltip
                                    />
                                </HStack>
                            ) : undefined
                        }
                        placeholder={placeholder}
                        hideArrow={hideArrow}
                    />
                )}
                <Picker.Popup
                    sideOffset={4}
                    side={side}
                    align={align}
                    aria-label={popupLabel}
                >
                    <Picker.Search placeholder="Search document categories..." />
                    <Picker.List>
                        {filteredCategories.map((category, index) => (
                            <Picker.Item
                                key={category.id}
                                value={category.id}
                                depth={
                                    category.parentCategoryId == null ? 0 : 1
                                }
                                treePosition={rowPlacements[index].treePosition}
                                leading={
                                    category.parentCategoryId == null ? (
                                        <DocumentCategoryIcon
                                            documentTaxonomy={
                                                category.documentTaxonomy
                                            }
                                        />
                                    ) : undefined
                                }
                            >
                                {category.label}
                            </Picker.Item>
                        ))}
                        {filteredCategories.length === 0 && (
                            <Picker.Empty>
                                No matching document categories
                            </Picker.Empty>
                        )}
                        {!hideFooter && canShowCta ? (
                            <Picker.Footer>
                                <Picker.Item
                                    leading={<PlusIcon />}
                                    onSelect={onCreateNew}
                                >
                                    {typedSearch
                                        ? `Create "${typedSearch}"`
                                        : "Create new document category"}
                                </Picker.Item>
                            </Picker.Footer>
                        ) : null}
                    </Picker.List>
                </Picker.Popup>
            </Picker.Select>
            <PlainModal
                show={showCreationModal}
                onHide={() => onCreationModalClosed()}
            >
                <CreateDocumentCategoryModal
                    categoryName={creationName}
                    onCategoryCreated={(category) =>
                        onCreationModalClosed(category)
                    }
                    onHide={() => onCreationModalClosed()}
                />
            </PlainModal>
        </>
    );
};
